import React from 'react';
import { Navigate, useLocation } from 'react-router';
import { useAuth } from '../context/AuthContext';

interface RequirePermissionProps {
  perm: string;
  children: React.ReactNode;
}

/**
 * Route wrapper that enforces a named permission.
 *
 * If the user is not logged in, redirect to /login (same as ProtectedRoute).
 * If the user is logged in but lacks the permission, render an access-denied
 * card instead of redirecting — a redirect would potentially loop if the
 * default landing page is also gated.
 */
const RequirePermission: React.FC<RequirePermissionProps> = ({ perm, children }) => {
  const { isLoggedIn, hasPermission } = useAuth();
  const location = useLocation();

  if (!isLoggedIn) {
    return <Navigate to="/login" state={{ from: location.pathname }} replace />;
  }

  if (!hasPermission(perm)) {
    return (
      <div className="max-w-2xl mx-auto mt-12 p-8 bg-card border border-border rounded-lg shadow-md text-center">
        <h2 className="text-2xl font-bold text-gray-800 mb-3">Access Denied</h2>
        <p className="text-gray-600 mb-2">
          You don't have permission to view this page.
        </p>
        <p className="text-sm text-muted-foreground">
          Missing permission: <code className="px-1.5 py-0.5 border border-status-neutral-border bg-status-neutral text-status-neutral-foreground rounded-sm font-mono text-xs">{perm}</code>
        </p>
        <p className="text-sm text-muted-foreground mt-4">
          If you believe you should have access, contact your administrator.
        </p>
      </div>
    );
  }

  return <>{children}</>;
};

export default RequirePermission;
